import type { ApiQueryParams } from '@/api/Api';

/**
 * Entity domains supported by the backend wrapper endpoints. These map 1:1 to
 * the `:scope` path segment of `/wrap/:scope/entities/*`.
 */
export type EntityScope =
  | 'project'
  | 'component'
  | 'reusable_component'
  | 'module'
  | 'section'
  | 'interface'
  | 'element'
  | 'entity'
  | 'enum_type'
  | 'page_element';

export type ServiceTypeAction =
  | 'list'
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'clone'
  | 'import'
  | 'export';

/**
 * Sensitive upstream identifiers (project/collection/service ids) are now
 * injected by the landing backend. They must never originate from the frontend,
 * so they are stripped from any params before a request is sent.
 */
export const REQUIRED_HIERARCHY_KEYS: Array<keyof ApiQueryParams> = [
  'project_id',
  'collection_key',
  'brand_service_id',
];

/**
 * Builds the non-sensitive query params for an entities request. The upstream
 * routing identifiers are resolved server-side from the scope, so this only
 * carries through caller-provided, non-secret params plus the JSON
 * base_type/export_type defaults that the upstream still expects.
 */
export const buildParams = (
  _scope: EntityScope,
  overrides?: Partial<ApiQueryParams>,
  serviceType?: ServiceTypeAction | string
): ApiQueryParams => {
  const resolved: ApiQueryParams = { ...(overrides ?? {}) };

  if (serviceType === 'export' && resolved.export_type === undefined) {
    resolved.export_type = 'json';
  }

  if (serviceType === 'import' && resolved.base_type === undefined) {
    resolved.base_type = 'json';
  }

  return resolved;
};

/**
 * @deprecated The backend now resolves all routing identifiers. Retained so any
 * caller can still detect explicitly-provided overrides if needed.
 */
export const extractApiQueryOverrides = (
  params?: Record<string, unknown>
): Partial<ApiQueryParams> | undefined => {
  if (!params) return undefined;
  const overrides: Partial<ApiQueryParams> = {};
  REQUIRED_HIERARCHY_KEYS.forEach((key) => {
    if (params[key] !== undefined) {
      overrides[key] = params[key] as string;
    }
  });
  return Object.keys(overrides).length ? overrides : undefined;
};

export const mergeApiQueryParams = <T extends ApiQueryParams>(
  scope: EntityScope,
  params: Partial<T> | undefined,
  serviceType?: ServiceTypeAction | string
): T => {
  const base = buildParams(scope, params, serviceType);
  return {
    ...base,
    ...(params ?? {}),
  } as unknown as T;
};

/**
 * @deprecated Service identifiers are resolved by the backend. Kept as a no-op
 * for backwards compatibility during the migration.
 */
export const getServiceTypeId = (
  _action: ServiceTypeAction
): string | undefined => undefined;

/**
 * @deprecated Collection identifiers are resolved by the backend. Kept as a
 * no-op for backwards compatibility during the migration.
 */
export const getSectionIdByScope = (
  _scope: EntityScope
): string | undefined => undefined;

/**
 * Filter parameter structure for list queries
 * Used to filter by parent entity fields (e.g., project_id, module_id)
 *
 * IMPORTANT: Standard query parameters (project_id, component_id, etc.) are for API routing
 * and come from environment variables. Filtering by parent's projectId should use
 * the filters array, which gets converted to fk[index][field] and fk[index][value] format.
 *
 * NOTE: The field name should be the database field name (e.g., 'project_id', not 'projectId')
 *
 * Example:
 * - Standard params: project_id, collection_key, brand_service_id (from env)
 * - Filtering: filters: [{ field: 'website_project_id', value: '123' }]
 *   becomes: fk[0][field]=project_id&fk[0][value]=123
 */
export interface FilterParam {
  field: string;
  value: string;
}

/**
 * Converts filter parameters to fk[index][field] and fk[index][value] format
 * for API query parameters. Axios will automatically serialize these to the correct
 * query string format: fk[0][field]=project_id&fk[0][value]=123
 *
 * Note: Field names should be database field names (e.g., 'project_id', not 'projectId')
 */
export const buildFilterParams = (
  filters?: FilterParam[]
): Record<string, string> => {
  if (!filters || filters.length === 0) {
    return {};
  }

  const filterParams: Record<string, string> = {};
  filters.forEach((filter, index) => {
    filterParams[`fk[${index}][field]`] = filter.field;
    filterParams[`fk[${index}][value]`] = filter.value;
  });

  return filterParams;
};
